import { memo } from "react";
import AppTabs, { AppTabProps } from "../AppTabs";
import useScreen from "@lib/hooks/useScreen";
import { AppLoader } from "..";
import { StyledFlexContainer } from "./index.styled";
import { Divider, Typography } from "antd";

export type AppResponsiveTabsProps<T = any> = {
  tabs: AppTabProps<T>[];
  loading?: boolean;
  selectedTab?: string;
  onChange?: (tab: string) => void;
  sticky?: boolean;
};

const AppResponsiveTabs = <T = any,>({
  tabs,
  loading = false,
  selectedTab: initialSelectedTab,
  onChange,
  sticky = false,
}: AppResponsiveTabsProps<T>) => {
  const { isMobile, loading: screenLoading } = useScreen();

  if (screenLoading) {
    return <AppLoader />;
  }

  // Mobile: Show tabs with navigation (only active tab content)
  if (isMobile) {
    return (
      <AppTabs
        tabs={tabs}
        loading={loading}
        selectedTab={initialSelectedTab}
        onChange={onChange}
        sticky={sticky}
        style={{ overflow: "hidden", height: "100%" }}
      />
    );
  }

  // Desktop: Show all tabs in flex mode (all tab contents visible)
  return (
    <StyledFlexContainer>
      {loading ? (
        <AppLoader />
      ) : (
        tabs.map((tab) => {
          const TabComponent = tab.Component;
          return (
            TabComponent && (
              <>
                <Divider orientation="center" style={{ margin: 0 }}>
                  <Typography.Text strong>{tab.Title}</Typography.Text>
                </Divider>
                <TabComponent
                  key={tab.key}
                  {...((tab.props as T) || ({} as T))}
                />
              </>
            )
          );
        })
      )}
    </StyledFlexContainer>
  );
};

export default memo(AppResponsiveTabs) as typeof AppResponsiveTabs;
AppResponsiveTabs.displayName = "AppResponsiveTabs";
